import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Form } from "react-bootstrap";
import { isMobile, parsePrice } from "../../../../utils/utils";
import { ORIGIN } from "../../../../utils/configs";
import "./UnifiedPricePicker.css";

function UnifiedPricePicker(props) {
  const { fieldLabel, onManualChange, setBtnDisabled } = props;
  const [openChart, setOpenChart] = useState(false);
  const [activeTimeframe, setActiveTimeframe] = useState(props.interval);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setActiveTimeframe(props.interval);
  }, [props.interval]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type !== "pick_price") return;
      const data = event.data.data;
      if (data?.pickerField !== fieldLabel) return;
      onManualChange(String(parsePrice(data.price)));
      setOpenChart(false);
    };

    if (openChart) {
      setBtnDisabled?.(isMobile());
      window.addEventListener("message", handleMessage);
    }
    return () => window.removeEventListener("message", handleMessage);
  }, [fieldLabel, onManualChange, openChart, setBtnDisabled]);

  const changeTimeframe = (timeframe) => {
    setActiveTimeframe(timeframe);
    setReloadKey((value) => value + 1);
  };

  const chartUrl = `${ORIGIN}/tvcharts?currency=${props.currency}&base=USDT`
    + `&intervals=${activeTimeframe}&mobile=true&insightsApp=true`
    + `&pickerField=${props.fieldLabel}&isSpot=false&draw=${props.draw}`
    + `&theme=${localStorage.getItem("theme")}`;

  const optionGroups = props.optionGroups || (props.options ? [{ options: props.options }] : []);

  return (
    <Col md={12}>
      <Form.Group className="mb-3">
        <Form.Label>{props.fieldLabel}</Form.Label>
        <div className="d-flex gap-2 mb-2">
          <Form.Control
            inputMode="decimal"
            value={props.manualValue ?? ""}
            placeholder="Exact price"
            onChange={(event) => props.onManualChange(event.target.value)}
          />
          <Button variant="outline-primary" onClick={() => setOpenChart((value) => !value)}>
            Pick
          </Button>
        </div>

        {optionGroups.map((group, groupIndex) => (
          <div key={group.label || groupIndex} className={groupIndex > 0 ? "mt-2" : undefined}>
            {group.label ? (
              <div className="small fw-semibold mb-1 unified-price-group-label">{group.label}</div>
            ) : null}
            <div className="d-flex flex-wrap">
              {(groupIndex === 0 && props.manualOptionLabel
                ? [props.manualOptionLabel, ...group.options]
                : group.options
              ).map((option) => {
                const isManualOption = option === props.manualOptionLabel;
                const isSelected = isManualOption
                  ? props.selectedOption == null
                  : props.selectedOption === option;
                const disabled = Boolean(props.isOptionDisabled?.(option));
                return (
                  <Button
                    key={option}
                    variant={isSelected ? "primary" : "outline-primary"}
                    active={isSelected}
                    disabled={disabled}
                    title={disabled ? (props.disabledOptionTitle?.(option) || "Select a candle to use this value") : undefined}
                    onClick={() => (isManualOption
                      ? props.onManualChange(props.manualValue)
                      : props.onOptionSelect(option))}
                    className="me-1 mb-1"
                    style={disabled ? { opacity: 0.45 } : undefined}
                    size="sm"
                  >
                    {option}
                  </Button>
                );
              })}
            </div>
          </div>
        ))}
      </Form.Group>

      {openChart && (
        <div className="mb-3">
          <ButtonGroup aria-label="Timeframe" className="mb-2">
            {["5M", "15M", "4H", "1D", "1W"].map((timeframe) => (
              <Button
                key={timeframe}
                variant={activeTimeframe === timeframe ? "primary" : "secondary"}
                onClick={() => changeTimeframe(timeframe)}
              >
                {timeframe}
              </Button>
            ))}
          </ButtonGroup>
          <iframe
            key={reloadKey}
            title={`${props.fieldLabel} price picker`}
            src={chartUrl}
            width="100%"
            height="420px"
            scrolling="no"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals"
          />
        </div>
      )}
    </Col>
  );
}

export default UnifiedPricePicker;
